import FormField from '@risk-smart/themed-cloudscape-components/form-field';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { capitalize } from 'lodash';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'src/components/form/select';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';

import {
  type ResultFamily,
  resultPermissionTypes,
} from './resultPermissionTypes';
import type { FormType } from './types';

interface Props {
  readOnly: boolean;
  onChange?: (val: FormType) => void;
  parent?: ObjectWithContributors;
  value?: FormType;
  testId?: string;
  /** Register whose result types gate the "rating" option. */
  resultFamily: ResultFamily;
}

const FindingTypeSelector: FC<Props> = ({
  readOnly,
  onChange,
  parent,
  value,
  testId,
  resultFamily,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'assessmentResults.fields',
  });
  const { t: tc } = useTranslation('common');
  const { t: tt } = useTranslation('taxonomy');

  const policyModuleEnabled = useIsModuleEnabled('document');
  const complianceModuleEnabled = useIsModuleEnabled('obligation');

  // Result types of the register being written — an oversight finding is
  // governed by `document_oversight_result` etc., not by the first-line
  // `document_assessment_result` (COI-639).
  const permissionTypes = resultPermissionTypes[resultFamily];

  const {
    hasPermission: canCreateDocumentAssessmentResult,
    loading: canCreateDocumentLoading,
  } = useHasPermissionQuery(`insert:${permissionTypes.document}`, parent);
  const {
    hasPermission: canCreateObligationAssessmentResult,
    loading: canCreateObligationLoading,
  } = useHasPermissionQuery(`insert:${permissionTypes.obligation}`, parent);
  const {
    hasPermission: canCreateRiskAssessmentResult,
    loading: canCreateRiskLoading,
  } = useHasPermissionQuery(
    [
      `insert:${permissionTypes.riskControlled}`,
      `insert:${permissionTypes.riskUncontrolled}`,
    ],
    parent
  );
  const {
    hasPermission: canCreateControlTestResult,
    loading: canCreateControlLoading,
  } = useHasPermissionQuery(`insert:${permissionTypes.controlTest}`, parent);

  const canCreateRating =
    (canCreateDocumentAssessmentResult &&
      !canCreateDocumentLoading &&
      policyModuleEnabled) ||
    (canCreateRiskAssessmentResult && !canCreateRiskLoading) ||
    (canCreateObligationAssessmentResult &&
      !canCreateObligationLoading &&
      complianceModuleEnabled) ||
    (canCreateControlTestResult && !canCreateControlLoading);
  const { hasPermission: canCreateAction, loading: canCreateActionLoading } =
    useHasPermissionQuery('insert:action', parent);

  const { hasPermission: canCreateIssue, loading: canCreateIssueLoading } =
    useHasPermissionQuery('insert:issue', parent);

  const options = [];

  if (canCreateRating) {
    options.push({
      value: 'rating',
      label: capitalize(tt('rating_one')),
    });
  }

  if (canCreateAction && !canCreateActionLoading) {
    options.push({
      value: Parent_Type_Enum.Action,
      label: capitalize(tt('action_one')),
    });
  }

  if (canCreateIssue && !canCreateIssueLoading) {
    options.push({
      value: Parent_Type_Enum.Issue,
      label: capitalize(tt('issue_one')),
    });
  }

  return (
    <FormField label={t('Type')} data-testid={`form-field-${testId}`}>
      <Select
        disabled={readOnly || options.length === 1}
        onChange={(e) => onChange?.(e.detail.selectedOption.value as FormType)}
        options={options}
        selectedOption={options.find((o) => o.value === value) || null}
        placeholder={t('Type_placeholder')}
        empty={tc('noMatchedFound')}
      />
    </FormField>
  );
};

export default FindingTypeSelector;
